/* ==========================================================================
   EZION — PRODUCT PAGE
   Gallery, variant picker, size chart, add-to-cart and the sticky buy bar.
   ========================================================================== */

(function () {
  'use strict';

  var EZ = window.EZION || (window.EZION = {});
  var DESKTOP = '(min-width: 990px)';

  /* ======================================================================
     <ez-gallery>
     One scroll-snap track serves both layouts: thumbnails drive it on
     desktop, a finger drives it on mobile, and both read the same index.
     ====================================================================== */

  class EzGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      if (!this.stage) return;

      this.slides = Array.from(this.stage.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.figure = this.querySelector('[data-fig]');
      this.hint = this.querySelector('[data-hint]');
      this.index = 0;

      this.thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', this.goTo.bind(this, i));
      }, this);

      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', this.goTo.bind(this, i));
      }, this);

      this.stage.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.paint();
    }

    goTo(index, event) {
      if (event) event.preventDefault();
      var slide = this.slides[index];
      if (!slide) return;
      this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft, behavior: 'smooth' });
      this.index = index;
      this.paint();
    }

    onScroll() {
      // Throttle to a frame — scroll fires far more often than we need.
      if (this.raf) return;
      this.raf = requestAnimationFrame(function () {
        this.raf = null;
        var width = this.stage.clientWidth;
        if (!width) return;
        var next = Math.round(this.stage.scrollLeft / width);
        if (next !== this.index && this.slides[next]) {
          this.index = next;
          this.paint();
        }
      }.bind(this));
    }

    /** Jump the gallery to whichever slide holds a variant's image. */
    showMedia(mediaId) {
      if (!mediaId) return;
      var target = this.slides.findIndex(function (slide) {
        return slide.dataset.mediaId === String(mediaId);
      });
      if (target > -1) this.goTo(target);
    }

    paint() {
      var index = this.index;
      var total = this.slides.length;
      var label = String(index + 1).padStart(2, '0');

      this.thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', String(i === index));
      });
      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', String(i === index));
      });

      if (this.figure && this.figure.dataset.figTemplate) {
        this.figure.textContent = this.figure.dataset.figTemplate.replace('__INDEX__', label);
      }
      if (this.hint && this.hint.dataset.hintTemplate) {
        this.hint.textContent = this.hint.dataset.hintTemplate
          .replace('__INDEX__', label)
          .replace('__TOTAL__', String(total).padStart(2, '0'));
      }
    }
  }

  /* ======================================================================
     <ez-product>
     Owns the form: variant resolution, price, availability, sticky bar.
     ====================================================================== */

  class EzProduct extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variant-json]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }

      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.gallery = this.querySelector('ez-gallery');
      this.optionInputs = Array.from(this.querySelectorAll('[data-option-input]'));

      this.bind();
      this.update(false);
    }

    bind() {
      this.optionInputs.forEach(function (input) {
        input.addEventListener('change', function () { this.update(true); }.bind(this));
      }, this);

      var chart = this.querySelector('[data-chart-toggle]');
      var panel = this.querySelector('[data-chart-panel]');
      if (chart && panel) {
        chart.addEventListener('click', function () {
          var open = !panel.hidden;
          panel.hidden = open;
          chart.textContent = open ? chart.dataset.showLabel : chart.dataset.hideLabel;
          chart.setAttribute('aria-expanded', String(!open));
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      this.bindQtyPlacement();
    }

    /**
     * One stepper, two homes. On a phone it belongs in the sticky bar; at every
     * width above that, back beside Add to bag. Note this is the phone
     * breakpoint, not the one where the sticky bar appears — the bar also shows
     * on tablets, but there the buy column still has room for the stepper.
     *
     * Moving the single node rather than rendering two keeps one
     * name="quantity" in the form — the input carries form="EzProductForm", so
     * it stays associated with the form even while it sits outside it in DOM.
     */
    bindQtyPlacement() {
      this.qty = this.querySelector('ez-qty');
      this.qtyHome = this.querySelector('[data-qty-home]');
      this.qtySlot = this.querySelector('[data-sticky-qty-slot]');
      if (!this.qty || !this.qtyHome || !this.qtySlot) return;

      this.qtyQuery = window.matchMedia('(max-width: 749px)');
      var place = this.placeQty.bind(this);
      place();

      // addEventListener on a MediaQueryList is not in older Safari.
      if (this.qtyQuery.addEventListener) {
        this.qtyQuery.addEventListener('change', place);
      } else if (this.qtyQuery.addListener) {
        this.qtyQuery.addListener(place);
      }
    }

    placeQty() {
      var target = this.qtyQuery.matches ? this.qtySlot : this.qtyHome;
      if (this.qty.parentNode === target) return;

      // Back beside Add to bag it goes first, so the button stays last.
      if (target === this.qtyHome) {
        target.insertBefore(this.qty, target.firstChild);
      } else {
        target.appendChild(this.qty);
      }
    }

    /** The option values currently selected, in option order. */
    selection() {
      var chosen = [];
      this.optionInputs.forEach(function (input) {
        if (input.checked) chosen[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return chosen;
    }

    match(selection) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (value, i) { return value === selection[i]; });
      });
    }

    update(userDriven) {
      var selection = this.selection();
      this.current = this.match(selection);

      this.paintAvailability(selection);
      this.paintPrice();
      this.paintCta();
      this.paintMeta();

      if (this.current) {
        if (this.idInput) this.idInput.value = this.current.id;
        if (userDriven) {
          if (this.gallery && this.current.featured_media) {
            this.gallery.showMedia(this.current.featured_media.id);
          }
          this.pushUrl(this.current.id);
        }
      }
    }

    /**
     * Grey out values that cannot be bought. A value is unavailable when no
     * variant carrying it — with every *earlier* option held as chosen — is
     * in stock, which is how a shopper reads the picker top to bottom.
     */
    paintAvailability(selection) {
      this.optionInputs.forEach(function (input) {
        var index = parseInt(input.dataset.optionIndex, 10);
        var probe = selection.slice(0, index);
        probe[index] = input.value;

        var reachable = this.variants.some(function (variant) {
          return variant.available && probe.every(function (value, i) {
            return value == null || variant.options[i] === value;
          });
        });

        input.disabled = !reachable;
        input.closest('[data-option-value]').classList.toggle('ez-opt__value--out', !reachable);
      }, this);
    }

    paintPrice() {
      var variant = this.current;
      var now = this.querySelector('[data-price-now]');
      var was = this.querySelector('[data-price-was]');
      var save = this.querySelector('[data-price-save]');

      if (!variant) return;

      if (now) now.textContent = EZ.formatMoney(variant.price);

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (was) {
        was.hidden = !onSale;
        if (onSale) was.textContent = EZ.formatMoney(variant.compare_at_price);
      }
      if (save) {
        save.hidden = !onSale;
        if (onSale) {
          save.textContent = (save.dataset.template || 'Save __AMOUNT__')
            .replace('__AMOUNT__', EZ.formatMoney(variant.compare_at_price - variant.price));
        }
      }

      var flash = this.querySelector('[data-flash]');
      if (flash) {
        if (onSale) {
          var percent = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          flash.hidden = false;
          flash.textContent = percent + '% OFF';
        } else {
          flash.hidden = true;
        }
      }
    }

    paintCta() {
      var button = this.querySelector('[data-add-button]');
      if (!button) return;

      var label = button.querySelector('[data-add-label]') || button;

      if (!this.current) {
        button.disabled = true;
        label.textContent = EZ.strings.unavailable || 'Unavailable';
      } else if (!this.current.available) {
        button.disabled = true;
        label.textContent = EZ.strings.soldOut || 'Sold out';
      } else {
        button.disabled = false;
        label.textContent = EZ.strings.add || 'Add to bag';
      }

      this.querySelectorAll('[data-sticky-label]').forEach(function (node) {
        node.textContent = label.textContent;
      });
      this.querySelectorAll('[data-sticky-cta]').forEach(function (node) {
        node.disabled = button.disabled;
      });
    }

    paintMeta() {
      var variant = this.current;
      if (!variant) return;

      var sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.hidden = !variant.sku;
      }

      var stock = this.querySelector('[data-stock]');
      if (stock && stock.dataset.template) {
        var count = variant.inventory_management
          ? Math.max(variant.inventory_quantity, 0)
          : null;
        if (count === null) {
          stock.hidden = true;
        } else {
          stock.hidden = false;
          stock.textContent = stock.dataset.template.replace('__COUNT__', count);
        }
      }
    }

    /** Keep the address bar on the chosen variant, without a reload. */
    pushUrl(id) {
      if (!window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    }

    onSubmit(event) {
      event.preventDefault();
      if (!this.current || !this.current.available) return;

      var qtyInput = this.querySelector('[data-qty-input]');
      var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      var button = this.querySelector('[data-add-button]');
      var label = button ? (button.querySelector('[data-add-label]') || button) : null;

      // The "buy now" button submits with name="return_to", which is what
      // sends a no-JS shopper straight to checkout. Read the same signal here.
      var submitter = event.submitter;
      var checkout = !!(submitter && submitter.name === 'return_to');

      if (label) label.textContent = EZ.strings.adding || 'Adding…';
      if (button) button.disabled = true;

      EZ.addToCart({ items: [{ id: this.current.id, quantity: quantity }] })
        .then(function (added) {
          if (checkout) {
            window.location.href = '/checkout';
            return;
          }
          // Back to the normal label rather than a lasting "in the bag",
          // which went stale as soon as the item was removed.
          this.paintCta();
          EZ.afterAdd(added);
        }.bind(this))
        .catch(function (error) {
          EZ.toast(error.message || EZ.strings.error, null);
          this.paintCta();
        }.bind(this))
        .then(function () {
          if (button) button.disabled = false;
        });
    }
  }

  /* ======================================================================
     Drawers
     The spec sheet, "why this one" and wash care ship open so desktop can
     render them as static blocks. Below the two-column breakpoint the ones
     that are meant to start closed get collapsed.
     ====================================================================== */

  function syncPanels() {
    var wide = window.matchMedia(DESKTOP).matches;

    document.querySelectorAll('[data-panel]').forEach(function (panel) {
      if (wide) {
        panel.open = true;
        return;
      }
      // Only reset a panel the shopper has not touched themselves.
      if (panel.dataset.touched) return;
      panel.open = panel.hasAttribute('data-default-open');
    });
  }

  document.addEventListener('toggle', function (event) {
    var panel = event.target.closest('[data-panel]');
    if (!panel) return;
    if (window.matchMedia(DESKTOP).matches) return;
    panel.dataset.touched = 'true';
    var mark = panel.querySelector('[data-panel-mark]');
    if (mark) mark.textContent = panel.open ? '–' : '+';
  }, true);

  window.addEventListener('resize', syncPanels);
  document.addEventListener('DOMContentLoaded', syncPanels);
  syncPanels();

  /* ---------------------------------------------------------------------- */

  /**
   * Build your own look.
   *
   * Totals whatever is ticked and adds it all in one request. The total is the
   * plain sum of real variant prices — no discount is applied or implied here,
   * because a theme cannot discount anything. Whatever saving the merchant has
   * set up as an automatic discount lands at checkout, which is what the note
   * under the button says.
   */
  class EzLook extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-look-item]'));
      this.totalNode = this.querySelector('[data-look-total]');
      this.wasNode = this.querySelector('[data-look-was]');
      this.saveNode = this.querySelector('[data-look-save]');
      this.button = this.querySelector('[data-look-add]');
      this.discount = parseInt(this.dataset.discount, 10) || 0;
      if (!this.items.length || !this.button) return;

      this.addEventListener('change', this.paint.bind(this));
      this.button.addEventListener('click', this.add.bind(this));
      this.paint();
    }

    /** [{id, price}] for every ticked piece. */
    chosen() {
      return this.items.reduce(function (out, item) {
        var check = item.querySelector('[data-look-check]');
        var variant = item.querySelector('[data-look-variant]');
        if (!check || !check.checked || !variant) return out;

        var price;
        if (variant.tagName === 'SELECT') {
          var option = variant.options[variant.selectedIndex];
          if (!option) return out;
          price = parseInt(option.dataset.price, 10);
        } else {
          price = parseInt(variant.dataset.price, 10);
        }

        out.push({ id: parseInt(variant.value, 10), price: price || 0 });
        return out;
      }, []);
    }

    paint() {
      var picked = this.chosen();
      var total = picked.reduce(function (sum, p) { return sum + p.price; }, 0);

      // The saving is advertised only once the look is actually a look, and
      // only if the merchant has declared one. Rounded to whole currency
      // units so the band never shows fractional paise.
      var pair = picked.length > 1;
      var saved = 0;
      if (this.discount > 0 && pair) {
        saved = Math.round(total * this.discount / 100 / 100) * 100;
      }

      if (this.totalNode) {
        this.totalNode.textContent = EZ.formatMoney(total - saved);
      }
      if (this.wasNode) {
        this.wasNode.hidden = saved === 0;
        this.wasNode.textContent = EZ.formatMoney(total);
      }
      if (this.saveNode) {
        this.saveNode.hidden = saved === 0;
        this.saveNode.textContent = (EZ.strings.lookSave || 'Save __AMOUNT__')
          .replace('__AMOUNT__', EZ.formatMoney(saved));
      }

      this.items.forEach(function (item) {
        var check = item.querySelector('[data-look-check]');
        item.toggleAttribute('data-off', !(check && check.checked));
      });

      // One piece is just the product they are already looking at.
      this.button.disabled = picked.length < 2;
    }

    add() {
      var picked = this.chosen();
      if (picked.length < 2) return;

      var label = this.querySelector('[data-look-label]');
      var original = label ? label.textContent : '';
      if (label) label.textContent = EZ.strings.lookAdding || 'Adding…';
      this.button.disabled = true;

      var self = this;
      EZ.addToCart({
        items: picked.map(function (p) { return { id: p.id, quantity: 1 }; })
      })
        .then(function () {
          if (label) label.textContent = original;
          self.button.disabled = false;
          EZ.toast(EZ.strings.lookAdded || 'Look added to bag', EZ.strings.viewBag);
        })
        .catch(function (error) {
          if (label) label.textContent = original;
          self.button.disabled = false;
          EZ.toast(error.message || EZ.strings.error, null);
        });
    }
  }

  if (!customElements.get('ez-gallery')) customElements.define('ez-gallery', EzGallery);
  if (!customElements.get('ez-product')) customElements.define('ez-product', EzProduct);
  if (!customElements.get('ez-look')) customElements.define('ez-look', EzLook);
})();
